import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, App, Avatar, Button, Card, Col, Descriptions, Form, Input, Row, Select, Spin, Tag, Typography } from 'antd'
import type { FormInstance } from 'antd'
import { EditOutlined, MailOutlined, PhoneOutlined, SaveOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons'
import { indiaLocationAPI, sellerAuthAPI, sellerProfileAPI } from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface SellerProfile {
  business_name: string
  owner_name: string
  business_email: string
  business_phone: string
  business_address?: string
  business_city?: string
  business_state?: string
  business_postal_code?: string
  approval_status: string
  gst_number?: string
  bank_account_number?: string
  bank_ifsc?: string
  bank_name?: string
  bank_details_missing?: boolean
  bank_details_message?: string | null
  created_at?: string
}

interface IndiaStateOption {
  name: string
  code: string
}

interface IndiaLocationOption {
  name: string
  state: string
  district?: string | null
  postal_code?: string | null
}

const postalPattern = /^[0-9A-Za-z-]{4,12}$/

const buildCityLabel = (city: IndiaLocationOption): string => {
  const parts = [city.name]

  if (city.district && city.district !== city.name) {
    parts.push(city.district)
  }

  if (city.postal_code) {
    parts.push(city.postal_code)
  }

  return parts.join(' • ')
}

const fillSellerProfileFromLocation = async (
  latitude: number,
  longitude: number,
  form: FormInstance<SellerProfile>,
  setCityOptions: React.Dispatch<React.SetStateAction<IndiaLocationOption[]>>,
): Promise<boolean> => {
  try {
    const response = await indiaLocationAPI.reverse({ latitude, longitude })
    const location = response.data.data

    if (!location) {
      return false
    }

    const stateName = location.state?.trim() || ''
    const cityName = location.city?.trim() || ''

    form.setFieldsValue({
      business_address: location.address || form.getFieldValue('business_address'),
      business_city: cityName || form.getFieldValue('business_city'),
      business_state: stateName || form.getFieldValue('business_state'),
      business_postal_code: location.postal_code || form.getFieldValue('business_postal_code'),
    })

    if (stateName) {
      setCityOptions([
        {
          name: cityName || stateName,
          state: stateName,
          district: location.district || null,
          postal_code: location.postal_code || null,
        },
      ])
    }

    return true
  } catch {
    return false
  }
}

const ProfilePage = () => {
  const [form] = Form.useForm<SellerProfile>()
  const [profile, setProfile] = useState<SellerProfile | null>(null)
  const [stateOptions, setStateOptions] = useState<IndiaStateOption[]>([])
  const [cityOptions, setCityOptions] = useState<IndiaLocationOption[]>([])
  const [statesLoading, setStatesLoading] = useState(false)
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const previousStateRef = useRef('')
  const previousCityRef = useRef('')
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()
  const selectedState = Form.useWatch('business_state', form) as string | undefined

  const canUpdateProfile = hasPermission('profile.update')
  const formDisabled = !editing || !canUpdateProfile

  useEffect(() => {
    let active = true

    const loadStates = async () => {
      setStatesLoading(true)

      try {
        const response = await indiaLocationAPI.states()

        if (active) {
          setStateOptions(response.data.data)
        }
      } catch {
        if (active) {
          message.warning('Unable to load Indian states right now. Manual entry remains available.')
        }
      } finally {
        if (active) {
          setStatesLoading(false)
        }
      }
    }

    void loadStates()

    return () => {
      active = false
    }
  }, [message])

  useEffect(() => {
    const state = String(selectedState ?? '').trim()
    const isKnownState = stateOptions.some((option) => option.name === state)
    const previousState = previousStateRef.current
    const stateChanged = previousState !== '' && previousState !== state

    previousStateRef.current = state

    if (stateChanged) {
      setCityOptions([])
      form.setFieldsValue({
        business_city: undefined,
      })
    }

    if (!state || !isKnownState) {
      return
    }

    let active = true

    const loadCities = async () => {
      setCitiesLoading(true)

      try {
        const response = await indiaLocationAPI.cities({ state, limit: 50 })

        if (active) {
          setCityOptions(response.data.data)
        }
      } catch {
        if (active) {
          message.warning('Unable to load cities for the selected state right now.')
        }
      } finally {
        if (active) {
          setCitiesLoading(false)
        }
      }
    }

    void loadCities()

    return () => {
      active = false
    }
  }, [form, message, selectedState, stateOptions])

  const fetchProfile = useCallback(async () => {
    setLoading(true)
    try {
      const [authResponse, profileResponse] = await Promise.all([sellerAuthAPI.profile(), sellerProfileAPI.show()])
      const mergedProfile = {
        ...profileResponse.data.data,
        ...authResponse.data.seller,
      }
      setProfile(mergedProfile)
      previousStateRef.current = String(mergedProfile.business_state ?? '').trim()
      previousCityRef.current = String(mergedProfile.business_city ?? '').trim()
      form.setFieldsValue(mergedProfile)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [form, message])

  useEffect(() => {
    void fetchProfile()
  }, [fetchProfile])

  const submit = async (values: SellerProfile) => {
    if (!canUpdateProfile) {
      message.warning('You are not permitted to update profile details.')
      return
    }

    setLoading(true)
    try {
      await Promise.all([
        sellerAuthAPI.updateProfile({
          owner_name: values.owner_name,
          business_name: values.business_name,
          business_phone: values.business_phone,
          business_address: values.business_address,
          business_city: values.business_city,
          business_state: values.business_state,
          business_postal_code: values.business_postal_code,
        }),
        sellerProfileAPI.updateBilling({
          gst_number: values.gst_number?.trim() || undefined,
          bank_account_number: values.bank_account_number?.trim() || undefined,
          bank_ifsc: values.bank_ifsc?.trim().toUpperCase() || undefined,
          bank_name: values.bank_name?.trim() || undefined,
        }),
      ])
      message.success('Profile updated successfully')
      setEditing(false)
      await fetchProfile()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  if (!profile && loading) {
    return <Spin size="large" />
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} xl={8}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar size={72} style={{ backgroundColor: '#1677ff' }}>
              {profile?.business_name?.charAt(0) ?? 'S'}
            </Avatar>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{profile?.business_name}</div>
              <Tag color={profile?.approval_status === 'approved' ? 'green' : 'orange'}>{profile?.approval_status}</Tag>
            </div>
          </div>

          <Descriptions bordered column={1} size="small" style={{ marginTop: 24 }}>
            <Descriptions.Item label="Owner">{profile?.owner_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{profile?.business_email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{profile?.business_phone}</Descriptions.Item>
            <Descriptions.Item label="GST">{profile?.gst_number || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Bank Account">{profile?.bank_account_number || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Bank IFSC">{profile?.bank_ifsc || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Bank Name">{profile?.bank_name || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Address">{profile?.business_address || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Location">
                {[
                  profile?.business_city,
                  profile?.business_state,
                  profile?.business_postal_code,
                ]
                  .filter((part): part is string => Boolean(part && part.trim()))
                  .join(' • ') || 'N/A'}
              </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      <Col xs={24} xl={16}>
        <Card
          title="Business Profile"
          extra={
            canUpdateProfile ? (
              <Button type="primary" icon={editing ? <SaveOutlined /> : <EditOutlined />} loading={loading} onClick={() => (editing ? form.submit() : setEditing(true))}>
                {editing ? 'Save Changes' : 'Edit Profile'}
              </Button>
            ) : (
              <Tag color="default">Read only</Tag>
            )
          }
        >
          <Form form={form} layout="vertical" onFinish={submit} disabled={formDisabled}>
            {profile?.bank_details_missing ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                title={profile.bank_details_message || 'Please add or update your bank details to request payouts.'}
              />
            ) : null}

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="owner_name" label="Owner Name" rules={[{ required: true, message: 'Owner name is required' }]}>
                  <Input prefix={<UserOutlined />} placeholder="Owner name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="business_name" label="Business Name" rules={[{ required: true, message: 'Business name is required' }]}>
                  <Input prefix={<ShopOutlined />} placeholder="Business name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="business_email" label="Business Email">
                  <Input prefix={<MailOutlined />} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="business_phone" label="Business Phone" rules={[{ required: true, message: 'Business phone is required' }]}>
                  <Input prefix={<PhoneOutlined />} placeholder="Phone number" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="business_address" label="Business Address">
              <Input.TextArea rows={3} placeholder="Full business address" />
            </Form.Item>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              title="This seller profile keeps one business address only. Use your current location to refresh it, then save changes to update the same address later."
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button
                disabled={formDisabled}
                onClick={async () => {
                  if (!navigator.geolocation) {
                    message.warning('Geolocation is not supported by your browser.')
                    return
                  }

                  message.loading({ content: 'Getting your current location...', key: 'seller-profile-location' })

                  navigator.geolocation.getCurrentPosition(
                    async (position) => {
                      const latitude = Number(position.coords.latitude.toFixed(7))
                      const longitude = Number(position.coords.longitude.toFixed(7))

                      const success = await fillSellerProfileFromLocation(
                        latitude,
                        longitude,
                        form,
                        setCityOptions,
                      )

                      if (success) {
                        message.success({ content: 'Current location loaded into your seller address.', key: 'seller-profile-location' })
                      } else {
                        message.warning({ content: 'Location found, but the seller address could not be resolved. Please edit manually.', key: 'seller-profile-location' })
                      }
                    },
                    () => {
                      message.error({ content: 'Unable to access your current location.', key: 'seller-profile-location' })
                    },
                    { enableHighAccuracy: true, timeout: 7000, maximumAge: 300000 },
                  )
                }}
              >
                Use current location
              </Button>
            </div>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="gst_number" label="GST Number">
                  <Input placeholder="GST number" />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Title level={5} style={{ marginTop: 8 }}>
              Payment Details
            </Typography.Title>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="bank_account_number" label="Bank Account Number">
                  <Input placeholder="Account number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="bank_ifsc" label="Bank IFSC Code">
                  <Input placeholder="IFSC code" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="bank_name" label="Bank Name">
                  <Input placeholder="Bank name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="business_state" label="State">
                  <Select
                    showSearch
                    allowClear
                    disabled={formDisabled || statesLoading}
                    placeholder="Select state"
                    options={[
                      ...stateOptions.map((state) => ({
                        label: `${state.name} (${state.code})`,
                        value: state.name,
                      })),
                      ...(form.getFieldValue('business_state')
                        ? [{ label: String(form.getFieldValue('business_state')), value: String(form.getFieldValue('business_state')) }]
                        : []),
                    ]}
                    onChange={() => {
                      form.setFieldsValue({ business_city: undefined, business_postal_code: undefined })
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="business_city" label="City" rules={[{ required: true, message: 'City is required' }]}>
                  <Select
                    showSearch
                    allowClear
                    disabled={formDisabled || !selectedState || citiesLoading}
                    placeholder={selectedState ? 'Select city' : 'Choose a state first'}
                    options={[
                      ...cityOptions.map((city) => ({
                        label: buildCityLabel(city),
                        value: city.name,
                      })),
                      ...(form.getFieldValue('business_city')
                        ? [{ label: String(form.getFieldValue('business_city')), value: String(form.getFieldValue('business_city')) }]
                        : []),
                    ]}
                    onChange={(value) => {
                      form.setFieldsValue({ business_city: value, business_postal_code: undefined })
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="business_postal_code"
                  label="Pincode"
                  rules={[
                    { required: true, message: 'Pincode is required' },
                    { pattern: postalPattern, message: 'Enter a valid pincode' },
                  ]}
                >
                  <Input placeholder="Enter pincode manually" disabled={formDisabled} />
                </Form.Item>
              </Col>
            </Row>

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8, marginBottom: 16 }}
              title="Pick the state first, then the city list will load. Enter the pincode manually."
            />
          </Form>
        </Card>
      </Col>
    </Row>
  )
}

export default ProfilePage
